/**
 * Holding Detail Header Component
 *
 * Displays the header section of the holding detail screen with:
 * - Fund name and AMC logo
 * - Current NAV and date
 * - Units held and current value
 * - Invested amount and P&L (with percentage)
 * - Rating badge (in-form, on-track, off-track, out-of-form)
 */

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINR, formatPercent, formatDate } from "@/lib/utils";
import { TrendingUp, TrendingDown, Building2 } from "lucide-react";
import type { FundRating } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Data required for the holding detail header
 */
export interface HoldingDetailHeaderData {
  /** Fund name */
  fundName: string;
  /** AMC (Asset Management Company) name */
  amcName: string;
  /** AMC logo URL (optional) */
  amcLogoUrl?: string;
  /** Current NAV */
  currentNav: number;
  /** NAV date (ISO string) */
  navDate: string;
  /** Units held */
  units: number;
  /** Current value in INR */
  currentValue: number;
  /** Invested amount in INR */
  investedAmount: number;
  /** Total returns in INR (absolute) */
  totalReturns: number;
  /** Total returns as percentage */
  totalReturnsPercent: number;
  /** Fund rating */
  rating: FundRating;
}

/**
 * Props for the HoldingDetailHeader component
 */
export interface HoldingDetailHeaderProps {
  /** Holding detail data */
  data: HoldingDetailHeaderData;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Status configuration with colors and labels
 */
const STATUS_CONFIG: Record<
  FundRating,
  {
    label: string;
    bgColor: string;
    textColor: string;
    badgeBg: string;
    badgeText: string;
    borderColor: string;
  }
> = {
  "in-form": {
    label: "In-Form",
    bgColor: "bg-emerald-500",
    textColor: "text-emerald-700 dark:text-emerald-400",
    badgeBg: "bg-emerald-100 dark:bg-emerald-900/30",
    badgeText: "text-emerald-700 dark:text-emerald-400",
    borderColor: "border-emerald-200 dark:border-emerald-800",
  },
  "on-track": {
    label: "On-Track",
    bgColor: "bg-sky-500",
    textColor: "text-sky-700 dark:text-sky-400",
    badgeBg: "bg-sky-100 dark:bg-sky-900/30",
    badgeText: "text-sky-700 dark:text-sky-400",
    borderColor: "border-sky-200 dark:border-sky-800",
  },
  "off-track": {
    label: "Off-Track",
    bgColor: "bg-amber-500",
    textColor: "text-amber-700 dark:text-amber-400",
    badgeBg: "bg-amber-100 dark:bg-amber-900/30",
    badgeText: "text-amber-700 dark:text-amber-400",
    borderColor: "border-amber-200 dark:border-amber-800",
  },
  "out-of-form": {
    label: "Out-of-Form",
    bgColor: "bg-rose-500",
    textColor: "text-rose-700 dark:text-rose-400",
    badgeBg: "bg-rose-100 dark:bg-rose-900/30",
    badgeText: "text-rose-700 dark:text-rose-400",
    borderColor: "border-rose-200 dark:border-rose-800",
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format NAV value with proper precision
 */
function formatNav(nav: number): string {
  return `₹${nav.toFixed(4)}`;
}

/**
 * Format units with proper precision
 */
function formatUnits(units: number): string {
  if (units >= 1000) {
    return units.toFixed(2);
  }
  return units.toFixed(4);
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * AMC Logo component with fallback
 */
function AmcLogo({
  amcName,
  logoUrl,
}: {
  amcName: string;
  logoUrl?: string;
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${amcName} logo`}
        className="h-10 w-10 rounded-lg object-contain bg-white dark:bg-gray-100 p-1"
        onError={(e) => {
          // Hide the image on error and show fallback
          e.currentTarget.style.display = "none";
        }}
      />
    );
  }

  // Fallback: Building icon
  return (
    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
      <Building2 className="h-5 w-5 text-muted-foreground" />
    </div>
  );
}

/**
 * Rating badge showing fund health status
 */
function RatingBadge({ rating }: { rating: FundRating }) {
  const config = STATUS_CONFIG[rating];
  return (
    <Badge
      variant="secondary"
      className={cn(
        "font-semibold border",
        config.badgeBg,
        config.badgeText,
        config.borderColor
      )}
    >
      {config.label}
    </Badge>
  );
}

/**
 * Value stat row component
 */
function StatRow({
  label,
  value,
  subValue,
  valueClassName,
}: {
  label: string;
  value: string;
  subValue?: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="text-right">
        <span className={cn("text-sm font-semibold", valueClassName)}>
          {value}
        </span>
        {subValue && (
          <span className="text-xs text-muted-foreground ml-1">
            {subValue}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Holding Detail Header
 *
 * Displays comprehensive holding information at the top of the detail view,
 * including fund identity, current valuation, and performance metrics.
 *
 * @example
 * ```tsx
 * <HoldingDetailHeader
 *   data={{
 *     fundName: "Parag Parikh Flexi Cap Fund - Direct Growth",
 *     amcName: "PPFAS Mutual Fund",
 *     amcLogoUrl: "/logos/ppfas.png",
 *     currentNav: 72.4521,
 *     navDate: "2024-01-15",
 *     units: 1354.892,
 *     currentValue: 98110,
 *     investedAmount: 77943,
 *     totalReturns: 20167,
 *     totalReturnsPercent: 25.87,
 *     rating: "in-form",
 *   }}
 * />
 * ```
 */
export function HoldingDetailHeader({
  data,
  className,
}: HoldingDetailHeaderProps) {
  const isPositiveReturn = data.totalReturns >= 0;
  const config = STATUS_CONFIG[data.rating];

  return (
    <Card className={cn("w-full overflow-hidden", className)}>
      <CardHeader className="pb-0">
        {/* Fund Identity Row */}
        <div className="flex items-start gap-3">
          <AmcLogo amcName={data.amcName} logoUrl={data.amcLogoUrl} />
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-base leading-tight line-clamp-2">
              {data.fundName}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {data.amcName}
            </p>
          </div>
          <RatingBadge rating={data.rating} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Current Value Section */}
        <div className="text-center py-4 border-b border-border/50">
          <p className="text-sm text-muted-foreground mb-1">Current Value</p>
          <p className="text-3xl font-bold tracking-tight">
            {formatINR(data.currentValue)}
          </p>
          <div
            className={cn(
              "flex items-center justify-center gap-1 mt-2",
              isPositiveReturn ? "text-green-600" : "text-red-600"
            )}
          >
            {isPositiveReturn ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
            <span className="text-sm font-semibold">
              {formatINR(data.totalReturns)} ({formatPercent(data.totalReturnsPercent)})
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Left Column */}
          <div className="space-y-3">
            <StatRow
              label="NAV"
              value={formatNav(data.currentNav)}
              subValue={`as of ${formatDate(data.navDate)}`}
            />
            <StatRow
              label="Units"
              value={formatUnits(data.units)}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-3">
            <StatRow
              label="Invested"
              value={formatINR(data.investedAmount)}
            />
            <StatRow
              label="P&L"
              value={formatINR(data.totalReturns)}
              valueClassName={isPositiveReturn ? "text-green-600" : "text-red-600"}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for HoldingDetailHeader
 */
export function HoldingDetailHeaderSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn("w-full overflow-hidden", className)}>
      <CardHeader className="pb-0">
        {/* Fund Identity Row Skeleton */}
        <div className="flex items-start gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Current Value Section Skeleton */}
        <div className="text-center py-4 border-b border-border/50">
          <Skeleton className="h-4 w-24 mx-auto mb-2" />
          <Skeleton className="h-8 w-40 mx-auto mb-2" />
          <Skeleton className="h-5 w-32 mx-auto" />
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-2 gap-4">
          {/* Left Column */}
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-baseline justify-between">
                <Skeleton className="h-4 w-12" />
                <div className="text-right space-y-1">
                  <Skeleton className="h-4 w-20" />
                  {i === 0 && <Skeleton className="h-3 w-16" />}
                </div>
              </div>
            ))}
          </div>

          {/* Right Column */}
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-baseline justify-between">
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default HoldingDetailHeader;
